import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { rupiah, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calculator, Plus, Trash2, ArrowRightLeft, Loader2 } from "lucide-react";
import KalkulatorBuilder from "@/components/KalkulatorBuilder";

// Walk-in-client quick estimate tool — same catalog/settings and the exact same
// KalkulatorBuilder UI as the one inside a lead (standalone=true prop), just with its own
// board and storage NOT tied to any lead. Built for the "client masih tanya-tanya, belum
// mau dicatat sebagai lead" moment: hitung cepat, kasih lihat, print/share — dan kalau
// akhirnya deal, "Jadikan Lead" memindahkannya ke Pipeline tanpa perlu input ulang.
export default function KalkulatorStandalone() {
  const qc = useQueryClient();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [converting, setConverting] = useState(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["documents-standalone"],
    queryFn: async () => (await api.get("/documents/standalone")).data,
  });

  const openNew = () => { setEditingDoc(null); setBuilderOpen(true); };
  const openEdit = (d) => { setEditingDoc(d); setBuilderOpen(true); };

  const remove = async (d) => {
    if (!window.confirm(`Hapus kalkulator untuk "${d.client?.nama || "klien ini"}"?`)) return;
    try {
      await api.delete(`/documents/standalone/${d.doc_id}`);
      toast.success("Dihapus");
      qc.invalidateQueries({ queryKey: ["documents-standalone"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const jadikanLead = async (d) => {
    setConverting(d.doc_id);
    try {
      const { data } = await api.post(`/documents/standalone/${d.doc_id}/jadikan-lead`, {});
      toast.success(`Jadi lead: ${data.nama}`);
      qc.invalidateQueries({ queryKey: ["documents-standalone"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setConverting(null); }
  };

  const totalFor = (d) => {
    const t = d.kalkulator_data?.totals;
    if (!t) return 0;
    if (t.opsi === 2) return Math.min(...(t.packages || []).map((p) => p.final || 0), 0) || (t.packages?.[0]?.final ?? 0);
    return t.final ?? t.mid ?? 0;
  };

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold font-heading tracking-tight flex items-center gap-2"><Calculator size={20} />Kalkulator</h1>
          <p className="text-sm text-zinc-500">Hitung estimasi cepat untuk client yang masih tanya-tanya — belum perlu dicatat sebagai lead.</p>
        </div>
        <Button data-testid="new-standalone-kalkulator" onClick={openNew} className="bg-blue-600 hover:bg-blue-700 h-9"><Plus size={16} className="mr-1" />Kalkulator Baru</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : docs.length === 0 ? (
        <div className="card-surface rounded-2xl p-10 text-center text-sm text-zinc-500 mt-6">
          Belum ada kalkulator berdiri sendiri. Klik "Kalkulator Baru" untuk mulai menghitung — cocok dipakai saat client visit ke kantor dan masih sekadar tanya-tanya harga.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {docs.map((d) => (
            <div key={d.doc_id} data-testid={`standalone-card-${d.doc_id}`} className="card-surface rounded-2xl p-4 hover:bg-white/[0.02]">
              <div onClick={() => openEdit(d)} className="cursor-pointer">
                <div className="font-semibold text-sm truncate">{d.client?.nama || "Tanpa nama"}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{d.client?.telp || "-"}</div>
                <div className="text-lg font-bold font-heading text-emerald-400 mt-2 tabular-nums">{rupiah(totalFor(d))}</div>
                <div className="text-[11px] text-zinc-600 mt-1">{fmtDate(d.created_at)}</div>
              </div>
              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/5">
                <Button onClick={() => jadikanLead(d)} disabled={converting === d.doc_id} variant="outline" size="sm" className="flex-1 h-8 text-xs bg-[#18181B] border-white/10">
                  {converting === d.doc_id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <ArrowRightLeft size={13} className="mr-1" />}Jadikan Lead
                </Button>
                <button onClick={() => remove(d)} title="Hapus" className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-white/5"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <KalkulatorBuilder standalone open={builderOpen} onOpenChange={setBuilderOpen} doc={editingDoc}
        onSaved={() => qc.invalidateQueries({ queryKey: ["documents-standalone"] })} />
    </div>
  );
}
